import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveSurveyResponse } from '../lib/database';

// Data for the steps
const usageOptions = ['For my Business', 'For my Clients', 'For my Personal use'];
const roleOptions = ['Founder', 'Marketer', 'Sales', 'Developer', 'Product Manager', 'Consultant', 'Other'];
const taskOptions = [
    'Engage with Customers',
    'Automate Internal Processes',
    'Access and Manage Data',
    'Integrate with Other Tools',
];
const channelOptions = [
    'Website (as a chat widget)',
    'WhatsApp',
    'Telegram',
    'Slack',
    'Instagram / Facebook',
    'Mobile App (iOS/Android)'
];
const TOTAL_STEPS = 5;

const OnboardingPage: React.FC = () => {
  const [step, setStep] = useState(0);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [answers, setAnswers] = useState({
    usage: '',
    role: '',
    agentTasks: [] as string[],
    otherTask: '',
    channels: [] as string[],
    integrations: '',
  });
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleAnswer = (field: keyof typeof answers, value: string | string[]) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiChoiceToggle = (field: 'agentTasks' | 'channels', value: string) => {
    const currentValues = answers[field] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    handleAnswer(field, newValues);
  };

  const handleFinish = async () => {
    if (user) {
      try {
        await saveSurveyResponse(user.id, 'onboarding', answers);
        localStorage.setItem('onboardingCompleted', 'true');
        navigate('/');
      } catch (error) {
        console.error("Failed to save survey.", error);
        // Optionally, show an error message to the user
      }
    }
  };

  const ProgressBar = () => (
    <div className="flex space-x-2">
      {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
        <div key={index} className="flex-1 h-1.5 rounded-full bg-gray-200">
          <div
            className={`h-full rounded-full ${step >= index ? 'bg-netlify-teal' : 'bg-gray-200'}`}
            style={{ transition: 'background-color 0.5s ease-in-out' }}
          ></div>
        </div>
      ))}
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0: // Usage & Role
        return (
          <div>
            <p className="text-sm font-semibold text-netlify-teal uppercase tracking-wider">Step 1 of {TOTAL_STEPS}</p>
            <div className="mt-8 space-y-10">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">How are you planning to use Open Agents?</h2>
                    <div className="mt-4 flex flex-wrap gap-3">
                    {usageOptions.map(option => (
                        <button key={option} onClick={() => handleAnswer('usage', option)} className={`px-5 py-2.5 border rounded-full transition-colors duration-200 font-medium ${answers.usage === option ? 'bg-netlify-teal text-white border-netlify-teal' : 'bg-white border-gray-300 hover:border-netlify-teal hover:text-netlify-teal text-gray-700'}`}>
                        {option}
                        </button>
                    ))}
                    </div>
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">What is your primary role?</h2>
                    <div className="mt-4 flex flex-wrap gap-3">
                    {roleOptions.map(option => (
                        <button key={option} onClick={() => handleAnswer('role', option)} className={`px-5 py-2.5 border rounded-full transition-colors duration-200 font-medium ${answers.role === option ? 'bg-netlify-teal text-white border-netlify-teal' : 'bg-white border-gray-300 hover:border-netlify-teal hover:text-netlify-teal text-gray-700'}`}>
                        {option}
                        </button>
                    ))}
                    </div>
                </div>
            </div>
          </div>
        );
      case 1: // Agent Task
        return (
          <div>
            <p className="text-sm font-semibold text-netlify-teal uppercase tracking-wider">Step 2 of {TOTAL_STEPS}</p>
            <h2 className="text-3xl font-bold text-gray-800 mt-2">What do you want your conversational agent to do?</h2>
            <p className="text-gray-600 mt-1">Select all that apply</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {taskOptions.map(option => (
                <button key={option} onClick={() => handleMultiChoiceToggle('agentTasks', option)} className={`px-5 py-2.5 border rounded-full transition-colors duration-200 font-medium ${answers.agentTasks.includes(option) ? 'bg-netlify-teal text-white border-netlify-teal' : 'bg-white border-gray-300 hover:border-netlify-teal hover:text-netlify-teal text-gray-700'}`}>
                  {option}
                </button>
              ))}
            </div>
            <div className="mt-6">
                <input type="text" value={answers.otherTask} onChange={e => handleAnswer('otherTask', e.target.value)} placeholder="Something else?" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-netlify-teal focus:border-netlify-teal sm:text-sm" />
            </div>
          </div>
        );
      case 2: // Channels
        return (
            <div>
                <p className="text-sm font-semibold text-netlify-teal uppercase tracking-wider">Step 3 of {TOTAL_STEPS}</p>
                <h2 className="text-3xl font-bold text-gray-800 mt-2">Where will your agent interact with users?</h2>
                <p className="text-gray-600 mt-1">Select all that apply</p>
                <div className="mt-8 flex flex-wrap gap-3">
                {channelOptions.map(option => (
                    <button key={option} onClick={() => handleMultiChoiceToggle('channels', option)} className={`px-5 py-2.5 border rounded-full transition-colors duration-200 font-medium ${answers.channels.includes(option) ? 'bg-netlify-teal text-white border-netlify-teal' : 'bg-white border-gray-300 hover:border-netlify-teal hover:text-netlify-teal text-gray-700'}`}>
                    {option}
                    </button>
                ))}
                </div>
            </div>
        );
      case 3: // Integrations
        return (
          <div>
            <p className="text-sm font-semibold text-netlify-teal uppercase tracking-wider">Step 4 of {TOTAL_STEPS}</p>
            <h2 className="text-3xl font-bold text-gray-800 mt-2">What will your agent connect to?</h2>
            <p className="text-gray-600 mt-1">Optional. List any tools or data sources you'd like to connect, separated by commas.</p>
            <div className="mt-8">
                <textarea
                    value={answers.integrations}
                    onChange={e => handleAnswer('integrations', e.target.value)}
                    placeholder="e.g., Zapier, Notion, Google Drive, a specific website, Airtable, HubSpot, Gmail, Google Calendar, custom API..."
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-netlify-teal focus:border-netlify-teal sm:text-sm"
                    rows={4}
                />
            </div>
          </div>
        );
      case 4: // Final Step
        return (
          <div>
            <h2 className="text-4xl font-bold text-gray-800">You're all set!</h2>
            <p className="text-lg text-gray-600 mt-4">As this is an early preview, your feedback is what shapes the future of this product. You have two ways to begin:</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button onClick={handleFinish} className="px-8 py-4 bg-netlify-teal text-white font-semibold rounded-lg shadow-md hover:bg-netlify-dark-teal transition-colors">
                Explore the App
              </button>
              <button onClick={() => setShowContactInfo(true)} className="px-8 py-4 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors">
                Book a free 15-min Onboarding Call
              </button>
            </div>
            {showContactInfo && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800">Contact us to book a call:</h3>
                <p className="mt-2 text-gray-600">
                  <a href="https://t.me/valerioneri" target="_blank" rel="noopener noreferrer" className="text-netlify-teal hover:underline">Telegram: @valerioneri</a>
                </p>
                <p className="mt-1 text-gray-600">
                  <a href="mailto:valerio.neri@columen.eu" className="text-netlify-teal hover:underline">Email: valerio.neri@columen.eu</a>
                </p>
              </div>
            )}
            <p className="mt-6 text-sm text-gray-500">On the call, we can set up your first agent together. Also, once you're in the app, look for the Roadmap page to vote on and suggest new features!</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <div className="flex-1 flex flex-col justify-start py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="w-full max-w-[75vw]">
          <div className="mb-8 flex items-center">
            <div className="w-12 h-12 bg-netlify-teal rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd"/></svg>
            </div>
            <h1 className="ml-4 text-4xl font-bold tracking-tight text-gray-900">Welcome to Open Agents</h1>
          </div>
          <div className="mb-8"><ProgressBar /></div>
          <div>{renderStep()}</div>
        </div>
        <div className="w-full max-w-[75vw] mt-12 flex items-center">
          {step > 0 && step < TOTAL_STEPS && (
            <button onClick={handleBack} className="text-sm font-medium text-gray-600 hover:text-gray-800 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back
            </button>
          )}
          {step < TOTAL_STEPS - 1 ? (
             <button onClick={handleNext} className="ml-auto px-6 py-2 bg-netlify-teal text-white font-semibold rounded-lg shadow-md hover:bg-netlify-dark-teal transition-colors">
                Continue
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
